import { clsx } from 'clsx';
import type { ReactElement } from 'react';
import { useCanvas } from '../state/canvas-store.js';
import type { AppState } from '@extapia/shared';

type Tool = AppState['currentTool'];

interface ToolDef {
  id: Tool;
  label: string;
  icon: ReactElement;
  shortcut?: string;
}

const IconSelection = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
  </svg>
);
const IconHand = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 11V6a2 2 0 0 0-4 0v5" /><path d="M14 10V4a2 2 0 0 0-4 0v6" /><path d="M10 10.5V6a2 2 0 0 0-4 0v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);
const IconRect = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /></svg>
);
const IconDiamond = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l10 10-10 10L2 12 12 2z" /></svg>
);
const IconEllipse = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="12" rx="9" ry="7" /></svg>
);
const IconArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="20" x2="20" y2="4" /><polyline points="14 4 20 4 20 10" /></svg>
);
const IconLine = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="20" x2="20" y2="4" /></svg>
);
const IconPen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></svg>
);
const IconText = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></svg>
);
const IconSticky = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h-8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9z" /><polyline points="15 3 15 9 21 9" /></svg>
);
const IconEraser = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H9L4 15l7-7 9 9-3 3z" /><line x1="15" y1="16" x2="9" y2="10" /></svg>
);

const TOOLS: ToolDef[] = [
  { id: 'selection', label: 'Select', icon: <IconSelection />, shortcut: 'V' },
  { id: 'hand', label: 'Pan', icon: <IconHand />, shortcut: 'H' },
  { id: 'rect', label: 'Rectangle', icon: <IconRect />, shortcut: 'R' },
  { id: 'diamond', label: 'Diamond', icon: <IconDiamond />, shortcut: 'D' },
  { id: 'ellipse', label: 'Ellipse', icon: <IconEllipse />, shortcut: 'O' },
  { id: 'arrow', label: 'Arrow', icon: <IconArrow />, shortcut: 'A' },
  { id: 'line', label: 'Line', icon: <IconLine />, shortcut: 'L' },
  { id: 'freehand', label: 'Draw', icon: <IconPen />, shortcut: 'P' },
  { id: 'text', label: 'Text', icon: <IconText />, shortcut: 'T' },
  { id: 'sticky', label: 'Sticky', icon: <IconSticky />, shortcut: 'S' },
  { id: 'eraser', label: 'Eraser', icon: <IconEraser />, shortcut: 'E' },
];

export const Toolbar = () => {
  const currentTool = useCanvas((s) => s.app.currentTool);
  const setTool = useCanvas((s) => s.setTool);

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 rounded-xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur shadow-lg ring-1 ring-black/5 px-2 py-1.5 flex items-center gap-1">
      {TOOLS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => setTool(t.id)}
          title={`${t.label}${t.shortcut ? ` (${t.shortcut})` : ''}`}
          className={clsx(
            'grid place-items-center w-9 h-9 rounded-lg transition-colors',
            currentTool === t.id
              ? 'bg-sky-500 text-white'
              : 'text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800',
          )}
        >
          {t.icon}
        </button>
      ))}
    </div>
  );
};
